import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import i18n, { changeLanguage } from "@/i18n";
import { apiErrorCode } from "@/lib/apiErrors";
import { loginWithPasskey as _loginWithPasskey } from "@/lib/passkeys";
import PasskeyNudgeModal from "@/components/PasskeyNudgeModal";
import { resetETMSummary } from "@/hooks/useETMSummary";
import { getTrustedDevice, setTrustedDevice } from "@/lib/trustedDevice";

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Cache the last-known user payload alongside the token. On boot (cold start
// after the PWA was killed, or pull-to-refresh on a flaky network) we hydrate
// from this cache so a transient /auth/me failure doesn't render an offline
// device as "logged out". The server is still source of truth — we refresh
// in the background and only clear on an explicit 401.
const USER_CACHE_KEY = "altiv_user_cache";
function _readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function _writeCachedUser(u) {
  try {
    if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch {}
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const initialToken = localStorage.getItem("altiv_token");
  const initialUser = initialToken ? _readCachedUser() : null;
  const [user, setUser] = useState(initialUser);
  const [token, setToken] = useState(initialToken);
  // If we already have a cached user, the app is usable instantly — no splash.
  // /auth/me will refresh in the background and correct anything stale.
  const [loading, setLoading] = useState(!initialUser);

  // When user data loads, resolve their UI language following the hierarchy:
  //   1. user.settings.language      — personal override (cross-device)
  //   2. company_primary_language    — company default
  //   3. localStorage / system       — whatever i18n already picked up
  useEffect(() => {
    if (!user) return;
    const personal = user.settings?.language;
    const companyDefault = user.company_primary_language;
    const resolved = [personal, companyDefault].find((l) => l && ["es", "en"].includes(l));
    if (resolved && i18n.language !== resolved) {
      // `changeLanguage` del módulo, no el de i18next: baja el catálogo antes
      // de cambiar. Este es el caso que más importa — el idioma personal o de
      // empresa se resuelve DESPUÉS del login, cuando el usuario ya arrancó en
      // otro idioma y ese catálogo puede no estar en memoria.
      changeLanguage(resolved);
      localStorage.setItem("altiv_language", resolved);
    }
  }, [user]);

  const logout = useCallback(
    (arg = null) => {
      // Back-compat: callers used to pass `reason` as a positional string.
      // New form is `logout({ reason, forced })`. `forced` (server kicked us
      // out — 401 / session replaced) is still accepted for call-site clarity
      // but no longer burns the trusted-device token: device trust is a 30-day
      // proof orthogonal to the session. It only dies on explicit user
      // revocation (Settings) or its own expiry — NOT when a session ends.
      const opts = typeof arg === "string" || arg === null ? { reason: arg } : arg;
      const { reason = null } = opts || {};

      // Fire-and-forget: tell the server to delete the session slot.
      // We don't await — the UI should clear instantly regardless of network.
      const currentToken = localStorage.getItem("altiv_token");
      if (currentToken) {
        axios
          .post(`${API}/auth/logout`, null, {
            headers: { Authorization: `Bearer ${currentToken}` },
            timeout: 5000,
          })
          .catch(() => {
            /* ignore — session TTL will clean up anyway */
          });
      }
      localStorage.removeItem("altiv_token");
      _writeCachedUser(null);
      if (reason) localStorage.setItem("altiv_logout_reason", reason);
      setToken(null);
      setUser(null);
      resetETMSummary();
      // Drop every cached query so the next user logging in on this tab
      // never sees the previous tenant's trips/expenses/etc. flashed in.
      try {
        queryClient.clear();
      } catch {}
      // Same posture for the PWA offline IndexedDB: snapshot + outbox both
      // live in `altiv-offline` (per-origin, NOT per-user) so on shared
      // devices (tablet rotada entre pilots) the next user would either
      // see the previous user's calendar offline or end up replaying their
      // queued writes under a fresh bearer. Fire-and-forget — UI shouldn't
      // wait on IDB to clear.
      import("@/lib/offline-db")
        .then(({ clearSnapshot, clearOutbox }) =>
          Promise.allSettled([clearSnapshot(), clearOutbox()])
        )
        .catch(() => {});
    },
    [queryClient]
  );

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
        _writeCachedUser(res.data);
      } catch (err) {
        console.error("Failed to refresh user:", err);
      }
    }
  }, [token]);

  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        const maxRetries = 3;
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const res = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000,
            });
            setUser(res.data);
            _writeCachedUser(res.data);
            setLoading(false);
            return;
          } catch (err) {
            console.error(`User load attempt ${attempt + 1}/${maxRetries} failed:`, err);

            // Retry on network/server errors
            if ((!err.response || err.response?.status >= 500) && attempt < maxRetries - 1) {
              await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
              continue;
            }

            // Auth error — only path that should log the user out.
            if (err.response?.status === 401) {
              const code = apiErrorCode(err);
              // Legacy responses returned a plain string in `detail`; keep the
              // substring fallback so older backends still trip the banner.
              const legacyDetail =
                typeof err.response?.data?.detail === "string" ? err.response.data.detail : "";
              const reason =
                code === "session_replaced" || legacyDetail.includes("Session replaced")
                  ? "session_replaced"
                  : null;
              logout({ reason, forced: true });
              break;
            }

            // Network / 5xx after retries: keep the cached user (if any) so
            // the offline PWA stays usable instead of bouncing to login.
            // The next online request will re-validate with the server.
            break;
          }
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token, logout]);

  // Applies the {token, user} payload returned by either /auth/login
  // (trusted device) or /auth/login/verify-2fa (after 2FA). Shared by
  // both flows so token storage / state stay in lockstep.
  //
  // Note: previously this also force-reset the theme to "dark" on every
  // login. Removed during the post-audit batch — theme is a per-device
  // preference and resetting it on every session was a surprise to
  // anyone who'd set "light". See ThemeContext for the new home.
  const _applySession = (data) => {
    const { token: t, user: u, trusted_device_token: tdt } = data;
    localStorage.setItem("altiv_token", t);
    // Cross-site cookie (Vercel↔Render) is unreliable, so the server also
    // returns the trusted-device token in the body. Stash it (keyed by this
    // account's email) so future /auth/login calls can present it and skip 2FA.
    // Scoping by email keeps each account's proof separate when several sign in
    // on the same browser — a single shared key let them overwrite each other.
    if (tdt) {
      setTrustedDevice(u?.email, tdt);
    }
    _writeCachedUser(u);
    setToken(t);
    setUser(u);
    return { success: true, user: u };
  };

  const login = async (email, password) => {
    const maxRetries = 3;
    let lastError;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // withCredentials:true so the browser sends/stores the
        // altiv_trusted_device cookie that lets us skip 2FA on return visits.
        // Also forward the localStorage-mirrored trusted-device token as a
        // body field — this is the reliable path when the cross-site cookie
        // gets dropped by the browser.
        const trustedDeviceToken = getTrustedDevice(email);
        const res = await axios.post(
          `${API}/auth/login`,
          { email, password, trusted_device_token: trustedDeviceToken || undefined },
          { timeout: 15000, withCredentials: true }
        );

        // Server may respond with a 2FA challenge instead of a session.
        // Pass it up so the UI can show the verification screen.
        if (res.data?.requires_2fa) {
          return {
            success: false,
            requires2fa: true,
            challengeId: res.data.challenge_id,
            emailMasked: res.data.email_masked,
          };
        }

        return _applySession(res.data);
      } catch (err) {
        lastError = err;
        console.error(`Login attempt ${attempt + 1}/${maxRetries} failed:`, err);

        const shouldRetry =
          !err.response ||
          err.response?.status === 503 ||
          err.response?.status === 502 ||
          err.response?.status === 504;

        if (shouldRetry && attempt < maxRetries - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }
        break;
      }
    }

    if (!lastError.response) {
      throw new Error(i18n.t("auth.errNetwork"));
    }
    if (lastError.response?.status === 503) {
      throw new Error(i18n.t("auth.errUnavailable"));
    }
    if (lastError.response?.status >= 500) {
      throw new Error(i18n.t("auth.errServer"));
    }

    throw lastError; // preserve status + detail so caller can localize (429, etc.)
  };

  const loginWithPasskey = async () => {
    // Passkey sign-in goes straight to a session — no password, no 2FA, no
    // trusted-device cookie needed (the passkey itself proves possession +
    // biometric = two factors by construction).
    const data = await _loginWithPasskey();
    return _applySession(data);
  };

  // Used by conditional-mediation flows (autofill-chip passkey pick) where
  // the page already obtained {token, user} and just needs to hydrate the
  // context state.
  const applyPasskeySession = (data) => _applySession(data);

  const verifyLoginCode = async (challengeId, code, trustDevice) => {
    // withCredentials:true so the server can set the trusted-device cookie
    // when trustDevice === true.
    const res = await axios.post(
      `${API}/auth/login/verify-2fa`,
      { challenge_id: challengeId, code, trust_device: !!trustDevice },
      { timeout: 15000, withCredentials: true }
    );
    return _applySession(res.data);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        verifyLoginCode,
        loginWithPasskey,
        applyPasskeySession,
        logout,
        loading,
        refreshUser,
      }}
    >
      {children}
      {/* Post-login passkey nudge — self-gates on user+browser support+count=0. */}
      <PasskeyNudgeModal user={user} token={token} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
